import { memo, useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import { ProntoAtendimentoPatientStep } from "@/components/schedule/prontoAtendimento/ProntoAtendimentoPatientStep";
import { useRequesterOptions } from "../hooks/useRequesterOptions";
import { useTeleconsultoriaBooking } from "../hooks/useTeleconsultoriaBooking";
import type { BookingPatient, BookingTarget, RequesterMode } from "../types";

interface BookTeleconsultoriaDialogProps {
  /** Sem alvo, o diálogo fica fechado. */
  target: BookingTarget | null;
  providerName: string;
  /** self = o próprio usuário (visão profissional); choose = escolhe na clínica. */
  requesterMode?: RequesterMode;
  onClose: () => void;
  /** Recebe o Appointment criado (opcional para quem só precisa recarregar). */
  onBooked: (appointment?: fhir5.Appointment) => void;
  /** Opcional: mostra "Trocar horário" (quem abriu volta à escolha de horário). */
  onChangeTarget?: () => void;
}

function targetSummary(target: BookingTarget): { when: string; teleconsultor: string } {
  if (target.mode === "now") {
    return { when: "Agora", teleconsultor: target.teleconsultor.display || "Teleconsultor" };
  }
  return {
    when: dayjs(target.start).format("DD/MM [às] HH:mm"),
    teleconsultor: target.agenda.teleconsultor.display || "Teleconsultor",
  };
}

/**
 * Marcar teleconsultoria em 2 passos: paciente → confirmar.
 * Na visão da clínica, o confirmar também pede o profissional solicitante.
 */
export const BookTeleconsultoriaDialog = memo(function BookTeleconsultoriaDialog({
  target,
  providerName,
  requesterMode,
  onClose,
  onBooked,
  onChangeTarget,
}: BookTeleconsultoriaDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { options, isClinicView, self } = useRequesterOptions(requesterMode);
  const { book, isBooking } = useTeleconsultoriaBooking();
  const [patient, setPatient] = useState<BookingPatient | null>(null);
  const [requesterReference, setRequesterReference] = useState("");

  // Cada abertura começa do passo do paciente.
  useEffect(() => {
    setPatient(null);
    setRequesterReference(options[0]?.reference || "");
  }, [target, options]);

  if (!target) return null;
  const summary = targetSummary(target);
  const requester = options.find((option) => option.reference === requesterReference);

  const handleClose = () => {
    if (!isBooking) onClose();
  };

  const handleConfirm = async () => {
    if (!patient || !requester) return;
    const appointment = await book({ target, patient, requester, selfReference: self.reference });
    if (appointment) onBooked(appointment);
  };

  return (
    <Dialog open onClose={handleClose} fullWidth maxWidth="sm" fullScreen={isMobile}>
      <DialogTitle>
        Teleconsultoria com {summary.teleconsultor} · {summary.when}
      </DialogTitle>
      <DialogContent dividers>
        {!patient ? (
          <ProntoAtendimentoPatientStep onContinue={(selected) => setPatient(selected)} />
        ) : (
          <Stack spacing={2}>
            <Stack spacing={0.5}>
              <Typography variant="body1" fontWeight={600}>
                Paciente: {patient.display}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Teleconsultor: {summary.teleconsultor} ({providerName})
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Quando: {summary.when} · por vídeo
              </Typography>
            </Stack>
            {isClinicView ? (
              <TextField
                select
                label="Profissional solicitante"
                value={requesterReference}
                onChange={(event) => setRequesterReference(event.target.value)}
                helperText="Quem acompanha o paciente na teleconsultoria."
              >
                {options.map((option) => (
                  <MenuItem key={option.reference} value={option.reference}>
                    {option.display}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Solicitante: {self.display}
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {onChangeTarget && (
          <Button
            onClick={onChangeTarget}
            disabled={isBooking}
            sx={{ textTransform: "none", mr: "auto" }}
          >
            Trocar horário
          </Button>
        )}
        {patient && (
          <Button onClick={() => setPatient(null)} disabled={isBooking} sx={{ textTransform: "none" }}>
            Trocar paciente
          </Button>
        )}
        <Button onClick={handleClose} disabled={isBooking} sx={{ textTransform: "none" }}>
          Cancelar
        </Button>
        {patient && (
          <Button
            variant="contained"
            onClick={() => void handleConfirm()}
            disabled={!requester || isBooking}
            sx={{ textTransform: "none" }}
          >
            {isBooking
              ? "Marcando..."
              : target.mode === "now" && requester?.reference === self.reference
                ? "Marcar e iniciar"
                : "Marcar teleconsultoria"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
});
